import * as Sentry from '@sentry/react'

import AppStore from '~publish/legacy/composer/composer/stores/AppStore'

import {
  deriveDisableDraftTransition,
  deriveEnableDraftTransition,
  deriveInitialOmniboxState,
} from './deriveModeTransition'
import type {
  ComposerRuntimeMode,
  DisableDraftTransitionInput,
  EnableDraftTransitionInput,
  InitialOmniboxStateInput,
  OmniboxState,
} from './deriveModeTransition'

/**
 * Observational parity checks for the mode-transition port
 * (`deriveModeTransition.ts`): compares its derived decision against Flux's
 * real outcome at each of the three call sites it mirrors. Never alters
 * behavior — Flux's decision always wins. Reports as `composer.bridge.drift`
 * (`track: mode`), same dashboard as the content/selection tracks.
 */

const reportDrift = (
  stage: 'init' | 'enable' | 'disable',
  extra: Record<string, unknown>,
): void => {
  const isProduction = process.env.NODE_ENV === 'production'

  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.error(
      `[composer.bridge] mode transition drift detected (${stage})`,
      JSON.stringify(extra),
    )
    return
  }

  const composerMode = AppStore.getOptions().composerMode ?? 'unknown'

  Sentry.captureEvent({
    message: 'composer.bridge.drift',
    level: 'warning',
    fingerprint: ['composer-bridge-drift', 'mode'],
    tags: {
      track: 'mode',
      stage,
      composerMode,
    },
    extra,
  })
  Sentry.addBreadcrumb({
    category: 'composer.bridge',
    level: 'warning',
    message: 'mode transition drift',
    data: {
      track: 'mode',
      stage,
      composerMode,
      ...extra,
    },
  })
}

// ~1% of production occurrences reported (0.04 local x 0.25 Sentry sample rate). Dev mode always reports.
const shouldSampleInProduction = (): boolean => {
  const isProduction = process.env.NODE_ENV === 'production'
  return !isProduction || Math.random() <= 0.04
}

/** Defaults composerMode to 'edit' when unset, matching the real comparisons. */
export function buildInitialOmniboxStateInput(
  input: Omit<InitialOmniboxStateInput, 'composerMode'> & {
    composerMode: ComposerRuntimeMode | undefined
  },
): InitialOmniboxStateInput {
  return { ...input, composerMode: input.composerMode ?? 'edit' }
}

/** Builds input from raw values snapshotted before the real enable logic mutates the draft. */
export function buildEnableDraftTransitionInput(
  isOmniboxEnabledBefore: OmniboxState,
  enabledDraftsBeforeCount: number,
  isSoleDraftEmptyBefore: boolean,
  selectedProfileCountAfter: number,
  isPreviewMode: boolean,
): EnableDraftTransitionInput {
  return {
    isOmniboxEnabledBefore,
    enabledDraftsBefore: {
      count: enabledDraftsBeforeCount,
      isSoleDraftEmpty:
        enabledDraftsBeforeCount === 1 && isSoleDraftEmptyBefore,
    },
    selectedProfileCountAfter,
    isPreviewMode,
  }
}

/** Builds `checkDisableDraftTransitionParity`'s input, with the same `composerMode` fallback as `buildInitialOmniboxStateInput`. */
export function buildDisableDraftTransitionInput(
  isOmniboxEnabledBefore: OmniboxState,
  enabledDraftsAfterCount: number,
  composerMode: ComposerRuntimeMode | undefined,
): DisableDraftTransitionInput {
  return {
    isOmniboxEnabledBefore,
    enabledDraftsAfter: { count: enabledDraftsAfterCount },
    composerMode: composerMode ?? 'edit',
  }
}

/**
 * Compared before any deferred selection dispatch fires. A `null` derived
 * result means "no decision yet, deferred to the enable-time port" — which
 * is exactly what the real value still reads at that point.
 */
export function checkInitialOmniboxParity(
  input: InitialOmniboxStateInput,
  actualIsOmniboxEnabled: OmniboxState,
): void {
  if (!shouldSampleInProduction()) return

  const derived = deriveInitialOmniboxState(input)
  if (derived === actualIsOmniboxEnabled) return

  reportDrift('init', { derived, actual: actualIsOmniboxEnabled, input })
}

export function checkEnableDraftTransitionParity(
  input: EnableDraftTransitionInput,
  actual: {
    isOmniboxEnabledAfter: OmniboxState
    didExpandJustEnabledDraft: boolean
  },
): void {
  if (!shouldSampleInProduction()) return

  const derived = deriveEnableDraftTransition(input)
  // A group enable settles several deferred calls together; only one member
  // actively decides isOmniboxEnabled, the rest derive a pass-through. Don't
  // flag a pass-through against the group's shared outcome — that mismatch
  // belongs to whichever sibling decided it. A solo enable has no sibling to
  // blame, so it's still checked.
  const isPartOfGroupSelection = input.selectedProfileCountAfter > 1
  const derivedMadeOmniboxDecision =
    derived.nextIsOmniboxEnabled !== input.isOmniboxEnabledBefore
  const omniboxMismatch =
    (derivedMadeOmniboxDecision || !isPartOfGroupSelection) &&
    derived.nextIsOmniboxEnabled !== actual.isOmniboxEnabledAfter
  const expandMismatch =
    derived.shouldExpandJustEnabledDraft !== actual.didExpandJustEnabledDraft
  if (!omniboxMismatch && !expandMismatch) return

  reportDrift('enable', {
    derivedIsOmniboxEnabled: derived.nextIsOmniboxEnabled,
    actualIsOmniboxEnabled: actual.isOmniboxEnabledAfter,
    derivedShouldExpand: derived.shouldExpandJustEnabledDraft,
    actualDidExpand: actual.didExpandJustEnabledDraft,
    input,
  })
}

export function checkDisableDraftTransitionParity(
  input: DisableDraftTransitionInput,
  actualIsOmniboxEnabledAfter: OmniboxState,
): void {
  if (!shouldSampleInProduction()) return

  const derived = deriveDisableDraftTransition(input)
  if (derived.nextIsOmniboxEnabled === actualIsOmniboxEnabledAfter) return

  reportDrift('disable', {
    derived: derived.nextIsOmniboxEnabled,
    actual: actualIsOmniboxEnabledAfter,
    input,
  })
}
